/**
 * moderationService.js - Bloqueio de usuário (Trust & Safety / UGC).
 *
 * Requisito do Google Play p/ app social: o usuário precisa poder BLOQUEAR quem o
 * incomoda. A fonte da verdade é a tabela `user_blocks` (migration 20260604120000);
 * o filtro de EXIBIÇÃO é aplicado nas leituras (getPosts/getComments/chat) usando o
 * conjunto de IDs bloqueados, mantido aqui em cache de memória (mesmo padrão do
 * userCacheService) para não rebuscar a cada query.
 *
 * IMPORTANTE: `clearBlockCache()` deve ser chamado no logout (AuthContext) — senão
 * o conjunto bloqueado de um usuário vazaria para o próximo login no mesmo aparelho.
 */

import { supabase } from '../lib/supabase';
import { getCachedUser } from './userCacheService';
import { captureException, captureMessage } from './telemetry';

// Set<string> de IDs bloqueados pelo usuário atual. null = ainda não carregado.
let blockedIdsCache = null;
// Promise em andamento (deduplicação, igual ao userCacheService).
let pendingLoad = null;

const EMPTY_SET = new Set();

// Teto p/ o carregamento do conjunto bloqueado. CRÍTICO: getBlockedUserIds é
// aguardado no caminho quente do feed (getPosts) e dos comentários (getComments),
// ANTES da query principal. Se a leitura travar (sessão não pronta no boot do APK,
// contenção de lock do GoTrue no Hermes, socket "conecta mas não responde"), o
// caminho inteiro ficaria 'pending' para sempre → skeleton eterno. O timeout
// converte o hang num fallback best-effort (Set vazio) sem prender a tela.
//
// Aumentado de 2500→4000: em cold-start de devices Android low-end, a restauração
// de sessão do GoTrue + query ao Supabase pode levar ~3s facilmente. 2.5s era
// agressivo demais e disparava block-load-timeout (QUIZFLOW-4) com frequência.
const BLOCK_LOAD_TIMEOUT_MS = 4000;

const buildAvatarUrl = (profile) => (
    profile?.foto_url
    || (profile?.avatar_seed
        ? `https://api.dicebear.com/7.x/fun-emoji/png?seed=${encodeURIComponent(profile.avatar_seed)}&size=80`
        : null)
);

const loadBlockedIds = async () => {
    // Sessão LOCAL (sem rede): só precisamos do id do usuário para o filtro. Evita
    // a chamada de rede `auth.getUser()` (do getCachedUser), que era o ponto de
    // trava no caminho quente do feed. Logado-out → sessão null → Set vazio.
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return new Set();

    const { data, error } = await supabase
        .from('user_blocks')
        .select('blocked_id')
        .eq('blocker_id', userId);

    if (error) return new Set();
    return new Set((data || []).map((row) => row.blocked_id));
};

// Corrida com timeout: resolve com Set vazio se loadBlockedIds não responder a tempo.
// Marca `timedOut` para o chamador decidir se persiste o resultado no cache.
const loadBlockedIdsWithTimeout = () => new Promise((resolve) => {
    let settled = false;
    const finish = (set, timedOut) => {
        if (settled) return;
        settled = true;
        resolve({ set, timedOut });
    };
    const timer = setTimeout(() => finish(new Set(), true), BLOCK_LOAD_TIMEOUT_MS);
    loadBlockedIds()
        .then((set) => { clearTimeout(timer); finish(set, false); })
        .catch((error) => {
            clearTimeout(timer);
            captureException(error, { tags: { area: 'getBlockedUserIds' } });
            finish(new Set(), false);
        });
});

/**
 * Conjunto de IDs bloqueados pelo usuário atual (async, com cache).
 * Logado-out → Set vazio. Usado pelos caminhos de leitura para filtrar autores.
 *
 * Garante NÃO bloquear o chamador por mais que BLOCK_LOAD_TIMEOUT_MS (o feed e os
 * comentários aguardam isto antes da query principal). Em timeout, retorna Set vazio
 * best-effort SEM gravar no cache → tenta de novo na próxima passada (cache aquece e
 * o filtro passa a valer). Erro real é reportado via telemetria.
 * @returns {Promise<Set<string>>}
 */
export const getBlockedUserIds = async () => {
    if (blockedIdsCache) return blockedIdsCache; // Set (mesmo vazio) é "carregado"
    if (pendingLoad) return pendingLoad;

    pendingLoad = (async () => {
        try {
            const { set, timedOut } = await loadBlockedIdsWithTimeout();
            // Só persiste no cache quando carregou de verdade. Timeout → mantém
            // blockedIdsCache=null para retentar depois (não envenena o cache).
            if (!timedOut) {
                blockedIdsCache = set;
            } else {
                captureMessage('block-load-timeout', {
                    tags: { area: 'getBlockedUserIds' },
                });
            }
            return set;
        } finally {
            pendingLoad = null;
        }
    })();

    return pendingLoad;
};

/**
 * Leitura síncrona, best-effort, do conjunto bloqueado (para filtros em render).
 * Retorna o cache se já carregado; senão Set vazio (e dispara o carregamento).
 * @returns {Set<string>}
 */
export const getBlockedUserIdsSync = () => {
    if (blockedIdsCache) return blockedIdsCache;
    // Aquece o cache para a próxima passada de render, sem bloquear esta.
    getBlockedUserIds();
    return EMPTY_SET;
};

/**
 * Bloquear um usuário. Idempotente (UNIQUE → 23505 tratado como sucesso).
 */
export const blockUser = async (blockedId) => {
    try {
        const user = await getCachedUser();
        if (!user) return { success: false, error: 'Você precisa estar logado para bloquear.' };
        if (!blockedId || blockedId === user.id) {
            return { success: false, error: 'Não é possível bloquear este usuário.' };
        }

        const { error } = await supabase
            .from('user_blocks')
            .insert({ blocker_id: user.id, blocked_id: blockedId });

        if (error && error.code !== '23505') throw error; // 23505 = já bloqueado

        // Atualiza o cache em memória para o filtro valer imediatamente.
        if (blockedIdsCache) blockedIdsCache.add(blockedId);
        else blockedIdsCache = new Set([blockedId]);

        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Desbloquear um usuário.
 */
export const unblockUser = async (blockedId) => {
    try {
        const user = await getCachedUser();
        if (!user) return { success: false, error: 'Você precisa estar logado.' };

        const { error } = await supabase
            .from('user_blocks')
            .delete()
            .eq('blocker_id', user.id)
            .eq('blocked_id', blockedId);

        if (error) throw error;

        if (blockedIdsCache) blockedIdsCache.delete(blockedId);

        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * @returns {Promise<boolean>} se `userId` está bloqueado pelo usuário atual.
 */
export const isUserBlocked = async (userId) => {
    if (!userId) return false;
    const set = await getBlockedUserIds();
    return set.has(userId);
};

/**
 * Lista de usuários bloqueados com dados de perfil (tela "Usuários bloqueados").
 * Duas queries (blocks + profiles), espelhando o padrão de getPosts.
 */
export const getBlockedProfiles = async () => {
    try {
        const user = await getCachedUser();
        if (!user) return [];

        const { data: blocks, error } = await supabase
            .from('user_blocks')
            .select('blocked_id, created_at')
            .eq('blocker_id', user.id)
            .order('created_at', { ascending: false });

        if (error || !blocks?.length) return [];

        const ids = blocks.map((b) => b.blocked_id);
        const { data: profiles } = await supabase
            .from('profiles')
            .select('id, nome, username, foto_url, avatar_seed')
            .in('id', ids);

        const profileMap = {};
        (profiles || []).forEach((p) => { profileMap[p.id] = p; });

        return blocks.map((b) => {
            const profile = profileMap[b.blocked_id] || {};
            return {
                id: b.blocked_id,
                displayName: profile.nome || 'Usuário',
                username: profile.username || null,
                avatarUrl: buildAvatarUrl(profile),
                blockedAt: b.created_at,
            };
        });
    } catch (error) {
        return [];
    }
};

/**
 * Limpa o cache de bloqueios. DEVE ser chamado no logout (AuthContext) para não
 * vazar a lista de um usuário para o próximo no mesmo aparelho.
 */
export const clearBlockCache = () => {
    blockedIdsCache = null;
    pendingLoad = null;
};

export default {
    getBlockedUserIds,
    getBlockedUserIdsSync,
    blockUser,
    unblockUser,
    isUserBlocked,
    getBlockedProfiles,
    clearBlockCache,
};
